/**
 * Ledger Reconciliation Audit View
 * Admin-only read-only audit: invoice→job linkage suggestions + ledger completeness gaps
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Refresh, AccountBalance, CheckCircle, PlayArrow } from "@mui/icons-material";
import { Link } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getLedgerReconciliationAudit,
  applyInvoiceJobLinks,
  backfillLedgerEntries,
} from "../services/ledgerReconciliationService.js";
import { openAppPopup } from "@/shared/utils/openAppPopup.js";
import {
  PageShell,
  PageHeader,
  PageFilters,
} from "@/shared/components/pageShell";

const safeNumber = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export default function LedgerReconciliationAuditView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [since, setSince] = useState("2026-01-25T00:00");
  const [sample, setSample] = useState(200);
  const [fullScan, setFullScan] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyError, setApplyError] = useState(null);

  const queryParams = useMemo(() => {
    const params = {
      sample: Math.min(Math.max(safeNumber(sample, 200), 1), 1000),
      fullScan: fullScan ? "true" : "false",
    };
    if (since) {
      // Backend expects ISO; datetime-local gives "YYYY-MM-DDTHH:mm"
      params.since = new Date(since).toISOString();
    }
    return params;
  }, [since, sample, fullScan]);

  const auditQuery = useQuery({
    queryKey: ["ledgerReconciliationAudit", queryParams],
    queryFn: () => getLedgerReconciliationAudit(queryParams),
  });

  const applyLinksMutation = useMutation({
    mutationFn: (params) => applyInvoiceJobLinks(params),
    onSuccess: (result) => {
      setApplyResult(result.data || result);
      setApplyError(null);
      // Refetch audit after successful apply
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["ledgerReconciliationAudit"] });
      }, 1000);
    },
    onError: (error) => {
      setApplyError(error.message || "Failed to apply invoice→job links");
      setApplyResult(null);
    },
  });

  const backfillMutation = useMutation({
    mutationFn: (params) => backfillLedgerEntries(params),
    onSuccess: (result) => {
      setApplyResult(result.data || result);
      setApplyError(null);
      // Refetch audit after successful backfill
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["ledgerReconciliationAudit"] });
      }, 1000);
    },
    onError: (error) => {
      setApplyError(error.message || "Failed to backfill ledger entries");
      setApplyResult(null);
    },
  });

  const payload = auditQuery.data?.success ? auditQuery.data.data : null;
  const linking = payload?.invoiceLinkingAudit || null;
  const ledger = payload?.ledgerCompletenessAudit || null;

  const suggestions = linking?.sample?.suggestions || [];
  const highConfidenceSuggestions = suggestions.filter((s) => s.confidence === "HIGH");
  const exceptions = linking?.sample?.exceptions || [];
  const gaps = ledger?.sampleGaps || [];

  const errorMessage =
    auditQuery.error?.message ||
    (auditQuery.data && auditQuery.data.success === false ? auditQuery.data.error : null);

  const handleApplyLinks = () => {
    setApplyResult(null);
    setApplyError(null);
    applyLinksMutation.mutate({
      dryRun: false,
      since: since ? new Date(since).toISOString() : undefined,
    });
  };

  const handleBackfill = () => {
    setApplyResult(null);
    setApplyError(null);
    backfillMutation.mutate({
      dryRun: false,
      since: since ? new Date(since).toISOString() : undefined,
    });
  };

  // Helper function to open related items in pop-up
  const openInPopup = (path) => {
    openAppPopup(path, { width: 1200, height: 800, title: "Bubakii" });
  };

  return (
    <PageShell sx={{ maxWidth: 1400, mx: "auto" }}>
      <PageHeader
        title="Ledger Reconciliation Audit"
        subtitle="Read-only audit: invoice→job suggestions + ledger completeness gaps."
        back={{
          label: "Back",
          shortLabel: "Back",
          onClick: () => navigate("/finance"),
        }}
        actions={
          <Tooltip title="Re-run audit">
            <IconButton onClick={() => auditQuery.refetch()} disabled={auditQuery.isFetching}>
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {applyError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApplyError(null)}>
          {applyError}
        </Alert>
      )}

      {applyResult && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setApplyResult(null)}
          icon={<CheckCircle />}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Operation completed successfully
          </Typography>
          {applyResult.processed !== undefined && (
            <Typography variant="body2">
              Processed: {applyResult.processed} | Updated: {applyResult.updated || 0} | Skipped:{" "}
              {applyResult.skipped || 0}
              {applyResult.errors?.length > 0 && ` | Errors: ${applyResult.errors.length}`}
            </Typography>
          )}
          {applyResult.invoicesProcessed !== undefined && (
            <Typography variant="body2">
              Invoices processed: {applyResult.invoicesProcessed} | Charges created:{" "}
              {applyResult.chargesCreated || 0} | Payments created: {applyResult.paymentsCreated || 0} |
              Refunds created: {applyResult.refundsCreated || 0}
              {applyResult.errors?.length > 0 && ` | Errors: ${applyResult.errors.length}`}
            </Typography>
          )}
        </Alert>
      )}

      <PageFilters>
        <TextField
          label="Since (go-live cutoff)"
          type="datetime-local"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 260, bgcolor: "background.paper" }}
        />
        <TextField
          label="Sample"
          type="number"
          value={sample}
          onChange={(e) => setSample(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ width: 140, bgcolor: "background.paper" }}
          inputProps={{ min: 1, max: 1000 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={fullScan}
              onChange={(e) => setFullScan(e.target.checked)}
            />
          }
          label="Full scan (slower)"
        />
        <Button
          variant="contained"
          startIcon={<AccountBalance />}
          onClick={() => auditQuery.refetch()}
          disabled={auditQuery.isFetching}
          sx={{ textTransform: "none" }}
        >
          Run audit
        </Button>
      </PageFilters>

      {payload && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generated: {payload.meta?.generatedAt || "-"} | Since:{" "}
          {payload.meta?.since || "-"}
        </Typography>
      )}

      {auditQuery.isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              mb: 2,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Invoice → Job linking (sample)
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap" mb={2} alignItems="center">
              <Chip label={`Suggestions: ${suggestions.length}`} color="success" variant="outlined" />
              <Chip
                label={`HIGH confidence: ${highConfidenceSuggestions.length}`}
                color="success"
                variant="filled"
              />
              <Chip label={`Exceptions: ${exceptions.length}`} color="warning" variant="outlined" />
              {linking?.invoicesMissingJobIdCount !== null && (
                <Chip
                  label={`Total missing jobId: ${linking.invoicesMissingJobIdCount}`}
                  color="info"
                  variant="outlined"
                />
              )}
              {highConfidenceSuggestions.length > 0 && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrow />}
                  onClick={handleApplyLinks}
                  disabled={applyLinksMutation.isPending}
                  sx={{ textTransform: "none", ml: "auto" }}
                >
                  {applyLinksMutation.isPending ? "Applying..." : `Apply ${highConfidenceSuggestions.length} links`}
                </Button>
              )}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Suggestions
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Quote</TableCell>
                    <TableCell>Suggested Job</TableCell>
                    <TableCell>Rule</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell align="right">Open</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No suggestions in sample</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    suggestions.map((row) => (
                      <TableRow key={`${row.invoiceId}-${row.rule}`}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => openInPopup(`/invoices/${encodeURIComponent(row.invoiceId)}`)}
                            sx={{ cursor: "pointer", textDecoration: "underline" }}
                          >
                            {row.invoiceId}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {row.quoteId ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => openInPopup(`/quotes/${encodeURIComponent(row.quoteId)}`)}
                              sx={{ cursor: "pointer", textDecoration: "underline" }}
                            >
                              {row.quoteId}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => openInPopup(`/finance/jobs/${encodeURIComponent(row.suggestedJobId)}/ledger`)}
                            sx={{ cursor: "pointer", textDecoration: "underline" }}
                          >
                            {row.suggestedJobId}
                          </Link>
                        </TableCell>
                        <TableCell>{row.rule}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.confidence || "UNKNOWN"}
                            color={row.confidence === "HIGH" ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openInPopup(`/finance/jobs/${encodeURIComponent(row.suggestedJobId)}/ledger`)}
                            sx={{ textTransform: "none" }}
                          >
                            Job ledger
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Exceptions
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Quote</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exceptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography color="text.secondary">No exceptions in sample</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    exceptions.map((row) => (
                      <TableRow key={`${row.invoiceId}-${row.quoteId || "no-quote"}`}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => openInPopup(`/invoices/${encodeURIComponent(row.invoiceId)}`)}
                            sx={{ cursor: "pointer", textDecoration: "underline" }}
                          >
                            {row.invoiceId}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {row.quoteId ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => openInPopup(`/quotes/${encodeURIComponent(row.quoteId)}`)}
                              sx={{ cursor: "pointer", textDecoration: "underline" }}
                            >
                              {row.quoteId}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{row.reason}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            sx={{
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Ledger completeness gaps (sample)
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap" mb={2} alignItems="center">
              <Chip label={`Gaps: ${gaps.length}`} color={gaps.length > 0 ? "error" : "success"} variant="outlined" />
              {ledger?.gapCounts && (
                <>
                  <Chip
                    label={`Missing jobId: ${ledger.gapCounts.missingJobIdForPostGoLiveEvent || 0}`}
                    color="warning"
                    variant="outlined"
                  />
                  <Chip
                    label={`Missing charges: ${ledger.gapCounts.missingCustomerCharge || 0}`}
                    color="error"
                    variant="outlined"
                  />
                  <Chip
                    label={`Payment mismatches: ${ledger.gapCounts.paymentCountMismatch || 0}`}
                    color="error"
                    variant="outlined"
                  />
                  <Chip
                    label={`Refund mismatches: ${ledger.gapCounts.refundCountMismatch || 0}`}
                    color="error"
                    variant="outlined"
                  />
                </>
              )}
              {gaps.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrow />}
                  onClick={handleBackfill}
                  disabled={backfillMutation.isPending}
                  sx={{ textTransform: "none", ml: "auto" }}
                >
                  {backfillMutation.isPending ? "Backfilling..." : "Backfill ledger entries"}
                </Button>
              )}
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Issue</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gaps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography color="text.secondary">No gaps in sample</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    gaps.map((row, idx) => (
                      <TableRow key={`${row.invoiceId}-${row.issue}-${idx}`}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => openInPopup(`/invoices/${encodeURIComponent(row.invoiceId)}`)}
                            sx={{ cursor: "pointer", textDecoration: "underline" }}
                          >
                            {row.invoiceId}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.issue} color="error" variant="outlined" />
                        </TableCell>
                        <TableCell>{row.reason}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </PageShell>
  );
}
